import React, { useState, useEffect } from 'react';
import { useNavigate, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { getAuth, signOut, onAuthStateChanged } from 'firebase/auth';
import { getFirestore, collection, query, where, getDocs } from 'firebase/firestore';
import './MaPage.css';
import MesInformations from '../components/MesInformations.tsx';
import Membres from '../components/Membres.tsx';
import AjoutMembre from '../components/AjoutMembre.tsx';
import Loges from '../components/Loges.tsx';
import AjoutLoges from '../components/AjoutLoges.tsx';
import ModifierMembre from '../components/ModifierMembre.tsx';
import ModifierLoge from '../components/ModifierLoge.tsx';
import MaLoge from '../components/MaLoge.tsx';
import AjoutMembreLoge from '../components/AjoutMembreLoge.tsx';
import Agenda from '../components/Agenda.tsx';
import LesLoges from '../components/LesLoges.tsx';
import ListeLoge from '../components/ListeLoge.tsx';
import Planche1 from '../components/Planche1.tsx';
import AjoutPlanche1 from '../components/AjoutPlanche1.tsx';
import Planche2 from '../components/Planche2.tsx';
import AjoutPlanche2 from '../components/AjoutPlanche2.tsx';
import Planche3 from '../components/Planche3.tsx';
import AjoutPlanche3 from '../components/AjoutPlanche3.tsx';
import Rituel1 from '../components/Rituel1.tsx';
import AjoutRituel1 from '../components/AjoutRituel1.tsx';
import Rituel2 from '../components/Rituel2.tsx';
import AjoutRituel2 from '../components/AjoutRituel2.tsx';
import Rituel3 from '../components/Rituel3.tsx';
import AjoutRituel3 from '../components/AjoutRituel3.tsx';
import Articles from '../components/Articles.tsx';
import AjoutArticle from '../components/AjoutArticle.tsx';
import ModifierArticle from '../components/ModifierArticle.tsx';
import Newsletter from '../components/Newsletter.tsx';
import Migration from "../Migration.tsx";


interface MenuItem {
  id: string;
  label: string;
  path?: string;
  requiredGrade?: string[];
  requiredFonction?: string[];
  requiredAdmin?: boolean;
}

interface UserPermissions {
  grade: string;
  fonction: string;
  isAdmin: boolean;
  userId: string;
  // ⭐ Fonction par loge (pour afficher les boutons selon la loge sélectionnée)
  fonctionsParLoge?: Record<string, string>;
}

function MaPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const [userPermissions, setUserPermissions] = useState<UserPermissions | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const auth = getAuth();
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (user) {
        try {
          const db = getFirestore();
          
          // Récupérer les informations du membre connecté
          const membresQuery = query(
            collection(db, 'Membres'),
            where('mail', '==', user.email)
          );
          
          const membresSnapshot = await getDocs(membresQuery);
          
          if (!membresSnapshot.empty) {
            const membreData = membresSnapshot.docs[0].data();
            
            // Log pour comprendre ce qui est récupéré
            console.log('Grade récupéré:', membreData.grades);
            console.log('Fonction récupérée:', membreData.fonction);
            console.log('Administrateur récupéré:', membreData.administrateur);
            
            setUserPermissions({
              grade: membreData.grades || '',
              fonction: membreData.fonction || '',
              isAdmin: membreData.administrateur === 'oui',
              userId: membresSnapshot.docs[0].id,
              // ⭐ Si le champ n'existe pas encore, on garde un fallback compatible
              fonctionsParLoge:
                (membreData.fonctionsParLoge && typeof membreData.fonctionsParLoge === 'object')
                  ? membreData.fonctionsParLoge
                  : (membreData.loge
                      ? { [membreData.loge]: (membreData.fonction || '') }
                      : undefined)
            });
          } else {
            console.error('Utilisateur non trouvé dans la collection Membres');
            navigate('/');
          }
        } catch (error) {
          console.error('Erreur lors de la récupération des permissions:', error);
        } finally {
          setLoading(false);
        }
      } else {
        // L'utilisateur n'est pas connecté, rediriger vers la page d'accueil
        navigate('/');
      }
    });

    return () => unsubscribe();
  }, [navigate]);

  const handleLogout = async () => {
    const auth = getAuth();
    try {
      await signOut(auth);
      navigate('/'); // Redirection vers la page d'accueil
    } catch (error) {
      console.error('Erreur lors de la déconnexion:', error);
    }
  };

  // Vérifier si l'utilisateur est Vénérable ou Secrétaire ou Admin
  const canEditContent = () => {
    const canEdit = userPermissions?.fonction === 'Vénérable' || 
                    userPermissions?.fonction === 'Secrétaire' || 
                    userPermissions?.isAdmin === true;
    console.log('MaPage - fonction:', userPermissions?.fonction);
    console.log('MaPage - isAdmin:', userPermissions?.isAdmin);
    console.log('MaPage - canEditContent() retourne:', canEdit);
    return canEdit;
  };

  // Définition des éléments du menu avec leurs restrictions
  const menuItems: MenuItem[] = [
    { id: 'mes-infos', label: 'Mes informations', path: '/mapage/mes-infos' },
    { id: 'ma-loge', label: 'Ma Loge', path: '/mapage/ma-loge' },
    { id: 'planche-1', label: 'Planche 1er', path: '/mapage/planche-1' },
    { 
      id: 'planche-2', 
      label: 'Planche 2eme', 
      path: '/mapage/planche-2',
      requiredGrade: ['compagnon', 'maître', 'maitre']
    },
    { 
      id: 'planche-3', 
      label: 'Planche 3eme', 
      path: '/mapage/planche-3',
      requiredGrade: ['maître', 'maitre']
    },
    { id: 'rituel-1', label: 'Rituel 1er', path: '/mapage/rituel-1' },
    { 
      id: 'rituel-2', 
      label: 'Rituel 2eme', 
      path: '/mapage/rituel-2',
      requiredGrade: ['compagnon', 'maître', 'maitre']
    },
    { 
      id: 'rituel-3', 
      label: 'Rituel 3eme', 
      path: '/mapage/rituel-3',
      requiredGrade: ['maître', 'maitre']
    },
    { id: 'agenda', label: 'Agenda', path: '/mapage/agenda' },
    { id: 'liste-loges', label: 'Liste Loges', path: '/mapage/liste-loges' },
    { id: 'separator', label: '---' },
    { 
      id: 'newsletter', 
      label: 'Envoyer Newsletter', 
      path: '/mapage/newsletter',
      requiredAdmin: true
    },
    { 
      id: 'membres', 
      label: 'Membres', 
      path: '/mapage/membres',
      requiredAdmin: true
    },
    { 
      id: 'loges', 
      label: 'Loges', 
      path: '/mapage/loges',
      requiredAdmin: true
    },
    { 
      id: 'articles', 
      label: 'Articles', 
      path: '/mapage/articles',
      requiredAdmin: true
    },
    { id: 'separator2', label: '---' },
    { id: 'logout', label: 'Déconnexion', path: 'logout' }
  ];

  const canAccessMenuItem = (item: MenuItem): boolean => {
    if (!userPermissions) return false;
    
    // Vérifier les restrictions de grade
    if (item.requiredGrade) {
      // Convertir tous les grades en minuscules pour la comparaison
      const userGrade = userPermissions.grade.toLowerCase().trim();
      const requiredGrades = item.requiredGrade.map(g => g.toLowerCase().trim());
      
      // Log pour déboguer
      console.log(`Vérification du grade pour ${item.id}:`, {
        userGrade,
        requiredGrades,
        hasAccess: requiredGrades.includes(userGrade)
      });
      
      if (!requiredGrades.includes(userGrade)) {
        return false;
      }
    }
    
    // Vérifier les restrictions de fonction
    if (item.requiredFonction && !item.requiredFonction.includes(userPermissions.fonction)) {
      return false;
    }
    
    // Vérifier les restrictions d'administrateur
    if (item.requiredAdmin && !userPermissions.isAdmin) {
      return false;
    }
    
    return true;
  };

  const handleNavigation = (path?: string) => {
    if (path === 'logout') {
      handleLogout();
    } else if (path) {
      navigate(path);
    }
  };

  const isActive = (path?: string) => {
    if (!path) return false;
    return location.pathname === path || location.pathname.startsWith(path);
  };

  if (loading) {
    return <div className="loading">Chargement...</div>;
  }
  
  // Calculer la valeur de canEdit pour être sûr qu'elle est correcte
  const canEdit = canEditContent();
  
  return (
    <div className="page-container">
      <nav className="sidebar">
        <ul className="menu-list">
          {menuItems.map((item) => {
            // Si c'est un séparateur, on l'affiche toujours
            if (item.id === 'separator' || item.id === 'separator2') {
              return <li key={item.id} className="menu-separator"></li>;
            }
            
            // Pour les autres éléments, on vérifie les permissions
            if (item.id === 'logout' || canAccessMenuItem(item)) {
              return (
                <li
                  key={item.id}
                  className={`menu-item ${isActive(item.path) ? 'active' : ''} ${item.id === 'logout' ? 'logout-item' : ''}`}
                  onClick={() => handleNavigation(item.path)}
                >
                  {item.label}
                </li>
              );
            }
            
            // Si l'utilisateur n'a pas les permissions, on n'affiche pas l'élément
            return null;
          })}
        </ul>
      </nav>
      <main className="main-content">
        <Routes>
          <Route path="/" element={<Navigate to="/mapage/mes-infos" replace />} />
          <Route path="mes-infos" element={<MesInformations />} />
          {userPermissions?.isAdmin && <Route path="membres" element={<Membres />} />}
         {userPermissions?.isAdmin && (<Route path="ajouter-membre" element={<AjoutMembre isAdmin={true} />} />
)}

          {/* ✅ AJOUTE ICI */}
          {userPermissions?.isAdmin && <Route path="migration" element={<Migration />} />}

          {/* Autoriser la modification si Admin OU (Vénérable/Secrétaire) */}
          <Route path="modifier-membre/:id/*" element={<ModifierMembre />} />

          {userPermissions?.isAdmin && <Route path="loges" element={<Loges />} />}
          {userPermissions?.isAdmin && <Route path="ajouter-loge" element={<AjoutLoges />} />}
          {/* Autoriser la modification de loge si Admin OU (Vénérable/Secrétaire) */}
          <Route path="modifier-loge/:id" element={<ModifierLoge />} />

          
          <Route
            path="ma-loge"
            element={
              <MaLoge
                // on garde canEdit pour compatibilité, mais MaLoge calcule désormais par loge sélectionnée
                canEdit={canEdit}
                userPermissions={userPermissions!}
              />
            }
          />
          
         <Route
            path="ajouter-membre-loge"
            element={<AjoutMembreLoge isAdmin={!!userPermissions?.isAdmin} />}
          />


          {/* Routes pour Planche 1 - Avec la prop canEdit */}
          <Route path="planche-1" element={<Planche1 canEdit={canEdit} />} />
          {canEdit && <Route path="ajouter-planche-1" element={<AjoutPlanche1 />} />}
          
          {/* Routes pour Planche 2 - accessibles seulement si l'utilisateur a le grade requis */}
          {(userPermissions?.grade?.toLowerCase() === 'compagnon' || 
            userPermissions?.grade?.toLowerCase() === 'maître' || 
            userPermissions?.grade?.toLowerCase() === 'maitre') && (
            <>
              <Route path="planche-2" element={<Planche2 canEdit={canEdit} />} />
              {canEdit && <Route path="ajouter-planche-2" element={<AjoutPlanche2 />} />}
            </>
          )}
          
          {/* Routes pour Planche 3 - accessibles seulement si l'utilisateur a le grade requis */}
          {(userPermissions?.grade?.toLowerCase() === 'maître' || 
            userPermissions?.grade?.toLowerCase() === 'maitre') && (
            <>
              <Route path="planche-3" element={<Planche3 canEdit={canEdit} />} />
              {canEdit && <Route path="ajouter-planche-3" element={<AjoutPlanche3 />} />}
            </>
          )}
          
          {/* Routes pour Rituel 1 */}
          <Route path="rituel-1" element={<Rituel1 canEdit={canEdit} />} />
          {canEdit && <Route path="ajouter-rituel-1" element={<AjoutRituel1 />} />}
          
          {/* Routes pour Rituel 2 - accessibles seulement si l'utilisateur a le grade requis */}
          {(userPermissions?.grade?.toLowerCase() === 'compagnon' || 
            userPermissions?.grade?.toLowerCase() === 'maître' || 
            userPermissions?.grade?.toLowerCase() === 'maitre') && (
            <>
              <Route path="rituel-2" element={<Rituel2 canEdit={canEdit} />} />
              {canEdit && <Route path="ajouter-rituel-2" element={<AjoutRituel2 />} />}
            </>
          )}
          
          {/* Routes pour Rituel 3 - accessibles seulement si l'utilisateur a le grade requis */}
          {(userPermissions?.grade?.toLowerCase() === 'maître' || 
            userPermissions?.grade?.toLowerCase() === 'maitre') && (
            <>
              <Route path="rituel-3" element={<Rituel3 canEdit={canEdit} />} />
              {canEdit && <Route path="ajouter-rituel-3" element={<AjoutRituel3 />} />}
            </>
          )}
          
          <Route path="agenda" element={<Agenda />} />
          <Route path="les-loges/:id/*" element={<LesLoges />} />
          <Route path="liste-loges" element={<ListeLoge />} />
          
          {/* Routes pour les utilisateurs admin */}
          {userPermissions?.isAdmin && (
            <>
              <Route path="articles" element={<Articles />} />
              <Route path="ajouter-article" element={<AjoutArticle />} />
              <Route path="modifier-article/:id" element={<ModifierArticle />} />
              <Route path="newsletter" element={<Newsletter />} />
            </>
          )}
          
          {/* Route de fallback - Rediriger vers la page Mes Informations si l'URL ne correspond à aucune route */}
          <Route path="*" element={<Navigate to="/mapage/mes-infos" replace />} />
        </Routes>
      </main>
    </div>
  );
}

export default MaPage;